import React, { useState } from 'react';
import { AlertTriangle, ListTodo, Moon, Users, UtensilsCrossed, ShoppingBasket } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { useFrameTime } from '@/components/ui/hover-time';
import {
  HeaderStatusDot,
  SAMPLE_DOT,
  STATUS_GREEN,
  type OverallTone,
} from '@/components/dashboard/statusTokens';
import { useT } from '@/contexts/I18nContext';
import { rackCategoryRank } from '@/lib/zoneComponents';
import { cn } from '@/lib/utils';
import { API_HOST } from '@/api/base';
import { stableImageUrl, toAbsoluteUrl } from '@/api/url';
import type { PortfolioTaskDto, PortfolioZoneDashboardDto } from '@/api/portfolio';
import { UnitDotPopover, popoverJustDismissed, type DotUnit } from './UnitDotPopover';
import { ProblemCarousel, type ProblemSlide, type TaskTile } from './ProblemCarousel';
import { Tooltip } from '@/components/ui/tooltip';

// Rich per-zone card for the «Все филиалы» dashboard: «биатлон» dot rows per
// monitored type on the left, a worst-first problem carousel on the right
// (photos + non-photo problems like overdue tasks), stacking on narrow cards
// via container queries. All-green zones collapse to the compact left-only
// form. Whole-point-dark and closed render as quiet muted states.

const imgSrc = (u: string) => stableImageUrl(toAbsoluteUrl(u, API_HOST));

// Worst-first weights for ordering the problem carousel. Echoes severityOf's
// ordering: lost revenue (over-limit queue, near-empty rack) outranks
// operational mess (dirty table), which outranks partials. Overdue tasks sit
// below dirty tables (photo evidence beats a counter) but above partials.
const SEV_QUEUE_RED = 40;
const SEV_RACK_GREY = 36;
const SEV_RACK_ORANGE = 30;
const SEV_TABLE_DIRTY = 25;
const SEV_TASKS_OVERDUE = 22;
const SEV_RACK_YELLOW = 15;
const SEV_QUEUE_YELLOW = 10;
// Only «в работе» left (no overdue) → tasks are a footnote, not a problem:
// below every real severity so the slide always lands last.
const SEV_TASKS_PENDING = 1;

// Task dots mirror the tasks page's semantics (OpsTasksPage statusMeta):
// grey = deadline not reached, amber = due soon, red = overdue, green = done.
const TASK_DOT: Record<PortfolioTaskDto['status'], string> = {
  created: 'bg-muted-foreground/50',
  pending: 'bg-muted-foreground/50',
  pre_deadline: 'bg-warning',
  overdue: 'bg-destructive',
  done: STATUS_GREEN,
};
const TASK_LABEL_KEY: Record<PortfolioTaskDto['status'], string> = {
  created: 'tasks.statusPending',
  pending: 'tasks.statusPending',
  pre_deadline: 'tasks.statusPreDeadline',
  overdue: 'tasks.statusOverdue',
  done: 'tasks.statusDone',
};
const isOpenTask = (tk: PortfolioTaskDto) => tk.status !== 'done' && tk.status !== 'overdue';

// Voice item 6 asks for "2-3" slides, not an endless reel — the dots convey
// the rest, and the drill-down has everything.
const MAX_PHOTO_SLIDES = 3;

interface Problem {
  unit: DotUnit;
  severity: number;
}

/** Строка раздела (#2323 «покрасивее»): иконка и название, точки по единицам и справа итог —
 *  сколько с проблемой из скольких, чтобы не считать точки глазами. */
function DotRow({ label, units, icon: Icon, bad }: { label: string; units: DotUnit[]; icon: React.ElementType; bad: number }) {
  return (
    <div className="flex items-start gap-2">
      <span className="flex w-[5.75rem] shrink-0 items-center gap-1.5 pt-0.5 text-xs text-muted-foreground">
        <Icon className="size-3.5 shrink-0" strokeWidth={1.75} />{label}
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center">
        {units.map(u => (
          <UnitDotPopover key={u.key} unit={u} />
        ))}
      </div>
      <span className={cn('shrink-0 pt-0.5 text-xs tabular-nums', bad > 0 ? 'font-medium text-amber-700 dark:text-amber-300' : 'text-muted-foreground')}>
        {bad > 0 ? `${bad} из ${units.length}` : `${units.length}`}
      </span>
    </div>
  );
}

const TONE_CHIP: Record<OverallTone, string> = {
  critical: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-950/50 dark:text-red-300',
  attention: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-950/50 dark:text-amber-300',
  ok: 'bg-success/10 text-success ring-success/20',
  none: 'bg-muted text-muted-foreground ring-border',
};

export function ZoneDashboardCard({
  zone,
  onOpen,
}: {
  zone: PortfolioZoneDashboardDto;
  onOpen: (zoneId: string) => void;
}) {
  const { t } = useT();
  const frameTime = useFrameTime();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const closed = zone.is_open === false;
  const dark = !zone.has_frames_today && !closed;
  // Camera-fed components this branch actually monitors — enumerated in the
  // dark panel so it says which data is missing (tasks are checklist-fed and
  // keep rendering, so a bare «нет данных» read as a contradiction).
  const darkAffected = [
    zone.racks.length > 0 ? t('portfolio.rowLayout') : null,
    zone.tables.length > 0 ? t('portfolio.rowTables') : null,
    zone.queues.length > 0 ? t('portfolio.rowQueues') : null,
  ].filter((s): s is string => s !== null).map(s => s.toLowerCase());

  // Canonical category order (готовая еда → выпечка → десерты → сопутка →
  // прочее), alphabetical inside a category — same order as the report cards.
  const orderedRacks = [...zone.racks].sort(
    (a, b) =>
      rackCategoryRank(a.rack_type) - rackCategoryRank(b.rack_type) ||
      (a.name || '').localeCompare(b.name || ''),
  );
  const rackUnits: DotUnit[] = orderedRacks.map(r => ({
    key: `rack-${r.id}`,
    name: r.name,
    dotClass: SAMPLE_DOT[r.status],
    statusLabel:
      r.issue_since && r.status !== 'green' && r.status !== 'unknown'
        ? `${t(`layout.status.${r.status}`)} · ${t('dashboard.issueSince', { time: frameTime(r.issue_since) })}`
        : t(`layout.status.${r.status}`),
    imageUrl: r.image_url ? imgSrc(r.image_url) : null,
    capturedAt: r.captured_at,
  }));

  const tableUnits: DotUnit[] = zone.tables.map(tb => {
    const noData = !tb.state || tb.state === 'no_data';
    // «состояние нельзя определить» (несколько столов / перекрыт): серая точка,
    // причина в подписи — стол не грязный и не чистый.
    const spoiled = tb.state === 'unknown';
    const moved = tb.state === 'moved' || tb.state === 'shifted';
    const reasonKey = `tables.spoiledReason.${tb.spoiled_reason}`;
    const reason = spoiled && tb.spoiled_reason ? t(reasonKey) : '';
    const unknownLabel = t('report.tables.stateUnknown')
      + (reason && reason !== reasonKey ? ` · ${reason}` : '');
    return {
      key: `table-${tb.id}`,
      name: tb.name,
      dotClass: noData || spoiled || moved || tb.recheck_pending ? SAMPLE_DOT.unknown : tb.state === 'dirty' ? SAMPLE_DOT.red : STATUS_GREEN,
      statusLabel: noData
        ? t('dashboard.status.noData')
        : tb.recheck_pending ? t('tables.rechecking')
          : spoiled ? unknownLabel
          : moved ? t('report.tables.stateShifted')
          : tb.state === 'dirty' ? t('tables.requireCleaning')
          : tb.state === 'occupied' ? t('report.tables.stateOccupied')
          : t('report.tables.stateFree'),
      imageUrl: tb.image_url ? imgSrc(tb.image_url) : null,
      capturedAt: tb.captured_at,
    };
  });

  const queueUnits: DotUnit[] = zone.queues.map(q => ({
    key: `queue-${q.id}`,
    name: q.name,
    dotClass: SAMPLE_DOT[q.status],
    statusLabel:
      q.status === 'unknown' || q.current === null
        ? t('queue.unknown')
        : t('queue.size', { n: q.current, max: q.max_allowed }),
    imageUrl: q.image_url ? imgSrc(q.image_url) : null,
    capturedAt: q.captured_at,
  }));

  const problems: Problem[] = [
    ...zone.queues.flatMap((q, i) =>
      q.status === 'red'
        ? [{ unit: queueUnits[i], severity: SEV_QUEUE_RED }]
        : q.status === 'yellow'
          ? [{ unit: queueUnits[i], severity: SEV_QUEUE_YELLOW }]
          : [],
    ),
    ...orderedRacks.flatMap((r, i) =>
      r.status === 'grey'
        ? [{ unit: rackUnits[i], severity: SEV_RACK_GREY }]
        : r.status === 'orange'
          ? [{ unit: rackUnits[i], severity: SEV_RACK_ORANGE }]
          : r.status === 'yellow'
            ? [{ unit: rackUnits[i], severity: SEV_RACK_YELLOW }]
            : [],
    ),
    ...zone.tables.flatMap((tb, i) =>
      tb.dirty ? [{ unit: tableUnits[i], severity: SEV_TABLE_DIRTY }] : [],
    ),
  ].sort((a, b) => b.severity - a.severity);

  // Branch has no active checklists → the tasks component doesn't exist here
  // (item 1 of the feedback batch). Zero at the source so tone/carousel agree.
  const tasksVisible = zone.checklists_configured !== false;
  // Per-task list (new BE, due_at order) → biathlon dots + carousel tile list.
  // Absent (old BE) → null, and the counts-based row/slide render instead.
  const taskItems = tasksVisible ? zone.tasks_items ?? null : null;
  const overdueTasks = (taskItems ?? []).filter(tk => tk.status === 'overdue');
  const openTasks = (taskItems ?? []).filter(isOpenTask);
  const overdue = taskItems ? overdueTasks.length : tasksVisible ? zone.tasks.overdue : 0;
  const pending = taskItems ? openTasks.length : tasksVisible ? zone.tasks.pending : 0;

  const taskUnits: DotUnit[] = (taskItems ?? []).map(tk => ({
    key: `task-${tk.id}`,
    name: tk.name,
    dotClass: TASK_DOT[tk.status],
    statusLabel: t(TASK_LABEL_KEY[tk.status]),
    imageUrl: null,
    // Mini-card's top-right HH:MM slot carries the DUE time for tasks.
    capturedAt: tk.due_at,
    photoless: true,
  }));

  // Carousel tile rows: overdue first, then still-open, each keeping due_at
  // order within its group.
  const taskTiles: TaskTile[] = [...overdueTasks, ...openTasks].map(tk => ({
    key: `task-${tk.id}`,
    name: tk.name,
    dotClass: TASK_DOT[tk.status],
    time: frameTime(tk.due_at),
  }));

  const captionOf = (u: DotUnit) =>
    [u.name, u.statusLabel, frameTime(u.capturedAt)].filter(Boolean).join(' · ');

  // Lightbox list == photo slides, worst-first, so slide order, photoIndex and
  // lightbox order all agree.
  const photos = problems.filter(p => p.unit.imageUrl).slice(0, MAX_PHOTO_SLIDES);
  const slides: ProblemSlide[] = dark
    ? [] // no frames → the quiet dark block is the story, not a carousel
    : [
        ...photos.map((p, i) => ({
          severity: p.severity,
          slide: {
            kind: 'photo',
            key: p.unit.key,
            imageUrl: p.unit.imageUrl!,
            caption: captionOf(p.unit),
            dotClass: p.unit.dotClass,
            photoIndex: i,
          } as const,
        })),
        ...(overdue > 0 || pending > 0
          ? [
              {
                severity: overdue > 0 ? SEV_TASKS_OVERDUE : SEV_TASKS_PENDING,
                slide: {
                  kind: 'tasks',
                  key: 'tasks',
                  overdue,
                  pending,
                  items: taskItems ? taskTiles : undefined,
                } satisfies ProblemSlide,
              },
            ]
          : []),
      ]
        .sort((a, b) => b.severity - a.severity)
        .map(e => e.slide);

  const hasUnits = rackUnits.length + tableUnits.length + queueUnits.length > 0;
  const tablesUnavailable = zone.tables.some(table => !table.state || table.state === 'no_data'
    || table.state === 'unknown' || table.recheck_pending);
  const maxSeverity = problems[0]?.severity ?? 0;
  const tone: OverallTone =
    overdue > 0 || maxSeverity >= SEV_TABLE_DIRTY
      ? 'critical'
      : maxSeverity > 0
        ? 'attention'
        : hasUnits && !tablesUnavailable
          ? 'ok'
          : 'none';
  const headerLabel = tone === 'ok' ? t('portfolio.allGreen') : t('portfolio.needsAttention');

  const showCarousel = !closed && slides.length > 0;

  const id = String(zone.zone_id);
  const handleOpen = () => {
    // A tap that dismissed a dot popover must not double as a drill-down.
    if (popoverJustDismissed()) return;
    // Selecting text on the card must not read as a drill-down click.
    if (document.getSelection()?.toString()) return;
    onOpen(id);
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={e =>
        e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ') && handleOpen()
      }
      className={cn(
        'cursor-pointer shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        !closed && !dark && tone === 'critical' && 'ring-1 ring-red-500/30',
        !closed && !dark && tone === 'attention' && 'ring-1 ring-amber-500/30',
      )}
    >
      {/* @container: the board decides card width, so the left/right split
          keys off the CARD's own width, not the viewport. */}
      <CardContent className="@container p-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <h3 className="truncate font-semibold tracking-tight">{zone.zone_name}</h3>
          {!closed && !dark && tone !== 'none' && (
            // Состояние словами, а не одной точкой (#2323): сколько проблем сейчас.
            <span className={cn('inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset', TONE_CHIP[tone])}>
              <span className={cn('size-1.5 rounded-full', tone === 'critical' ? 'bg-red-500' : tone === 'attention' ? 'bg-amber-500' : 'bg-success')} />
              {tone === 'ok' ? headerLabel : `${problems.length + overdue} ${problems.length + overdue === 1 ? 'проблема' : problems.length + overdue < 5 ? 'проблемы' : 'проблем'}`}
            </span>
          )}
        </div>

        {closed ? (
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <Moon className="size-4 shrink-0" />
            {t('portfolio.zoneClosed')}
          </div>
        ) : (
          <div className={cn('mt-3 grid gap-x-4 gap-y-3', showCarousel && '@md:grid-cols-2')}>
            {/* Left half: state blocks + biathlon rows */}
            <div className="min-w-0 space-y-3">
              {dark && (
                <div className="flex items-start gap-2 rounded-lg bg-muted/60 p-3 text-sm text-muted-foreground">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
                  <span>
                    {zone.has_local_computer
                      ? t('portfolio.zoneDarkLc')
                      : t('portfolio.zoneDarkNoLc')}
                    {/* Name exactly WHICH data is missing — the camera-fed
                        components — so the panel can't read as contradicting a
                        live tasks row next to it (tasks aren't camera-fed). */}
                    {darkAffected.length > 0 && (
                      <span className="mt-0.5 block text-xs text-muted-foreground/80">
                        {t('portfolio.zoneDarkAffected', { components: darkAffected.join(', ') })}
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Tasks: not frame-based, so they render even on a dark point.
                  New BE → per-task biathlon dots (incl. done, so the row reads
                  as progress); old BE → the counts summary. */}
              {taskItems ? (
                taskItems.length > 0 && (
                  <DotRow label={t('portfolio.rowTasks')} units={taskUnits} icon={ListTodo} bad={overdueTasks.length} />
                )
              ) : (
                (overdue > 0 || pending > 0) && (
                  <div className="flex items-center gap-2">
                    <span className="w-[5.75rem] shrink-0 text-xs text-muted-foreground">
                      {t('portfolio.rowTasks')}
                    </span>
                    <span className="inline-flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                      <span
                        className={cn(
                          'size-2.5 shrink-0 rounded-full',
                          overdue > 0 ? SAMPLE_DOT.red : STATUS_GREEN,
                        )}
                      />
                      <span className="truncate">
                        {[
                          overdue > 0 ? t('portfolio.overdue', { n: overdue }) : null,
                          pending > 0 ? t('portfolio.pending', { n: pending }) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </div>
                )
              )}

              {!dark && (
                <>
                  {tableUnits.length > 0 && (
                    <DotRow label={t('portfolio.rowTables')} units={tableUnits} icon={UtensilsCrossed} bad={zone.tables.filter(tb => tb.dirty).length} />
                  )}
                  {rackUnits.length > 0 && (
                    <DotRow label={t('portfolio.rowLayout')} units={rackUnits} icon={ShoppingBasket} bad={zone.racks.filter(r => r.status !== 'green' && r.status !== 'unknown').length} />
                  )}
                  {queueUnits.length > 0 && (
                    <DotRow label={t('portfolio.rowQueues')} units={queueUnits} icon={Users} bad={zone.queues.filter(q => q.status === 'red' || q.status === 'yellow').length} />
                  )}
                  {!hasUnits && overdue === 0 && pending === 0 && (taskItems?.length ?? 0) === 0 && (
                    <div className="text-sm text-muted-foreground">
                      {t('dashboard.status.noData')}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right half: the biggest problems, one at a time. */}
            {/* Фото проблем — коллажем одной высоты, без белых полей вокруг узких кропов (#2334);
                задачи без фото — прежней карточкой. */}
            {showCarousel && (
              <div className="min-w-0 space-y-2 self-start">
                {photos.length > 0 && (
                  <PhotoCollage items={photos.map(p => ({ key: p.unit.key, src: p.unit.imageUrl!, caption: captionOf(p.unit), dotClass: p.unit.dotClass }))} onOpen={setLightboxIndex} />
                )}
                {slides.some(s => s.kind !== 'photo') && (
                  <ProblemCarousel slides={slides.filter(s => s.kind !== 'photo')} onOpenPhoto={setLightboxIndex} />
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {lightboxIndex !== null && (
        // The lightbox mounts inline (not portaled) — fence its clicks/keys off
        // the card's drill-down handlers.
        <div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
          <ImageLightbox
            srcs={photos.map(p => p.unit.imageUrl!)}
            index={lightboxIndex}
            alt={photos[lightboxIndex]?.unit.name}
            captions={photos.map(p => captionOf(p.unit))}
            onClose={() => setLightboxIndex(null)}
            onNavigate={setLightboxIndex}
          />
        </div>
      )}
    </Card>
  );
}

/** Коллаж фото проблем: ширина каждого кадра пропорциональна его соотношению сторон, поэтому
 *  все одной высоты и без полей; не кропаем. Узкие кропы не растягиваются на всю ширину
 *  карточки — ряд ограничен высотой ~180 px. Клик — лайтбокс. */
function PhotoCollage({ items, onOpen }: { items: { key: string; src: string; caption: string; dotClass: string }[]; onOpen: (i: number) => void }) {
  const [ar, setAr] = useState<Record<string, number>>({});
  const sum = items.reduce((acc, p) => acc + (ar[p.key] ?? 1.33), 0);
  return (
    <div className="flex items-start gap-1.5" style={{ maxWidth: Math.round(sum * 180) }}>
      {items.map((p, i) => (
        <Tooltip key={p.key} content={p.caption}><button type="button" aria-label={p.caption}
          onClick={e => { e.stopPropagation(); onOpen(i); }}
          className="relative min-w-0 overflow-hidden rounded-md ring-1 ring-border/60 transition hover:ring-primary/50"
          style={{ flex: `${ar[p.key] ?? 1.33} 1 0%` }}>
          <img src={p.src} alt={p.caption} loading="lazy" className="block h-auto w-full"
            onLoad={e => {
              const im = e.currentTarget;
              if (im.naturalWidth && im.naturalHeight) setAr(a => ({ ...a, [p.key]: im.naturalWidth / im.naturalHeight }));
            }} />
          <span className={cn('absolute left-1.5 top-1.5 size-2 rounded-full ring-2 ring-white', p.dotClass)} />
        </button></Tooltip>
      ))}
    </div>
  );
}
